<template>
  <div class="address-fields">
    <!-- Country -->
    <div class="form-group">
      <label :for="`${prefix}-country`">
        Country <span v-if="required" class="required-star">*</span>
      </label>
      <select
        :id="`${prefix}-country`"
        :value="modelValue.country"
        class="form-control"
        :required="required"
        @change="onCountryChange($event.target.value)"
      >
        <option value="">-- Select Country --</option>
        <option v-for="c in COUNTRIES" :key="c.value" :value="c.value">
          {{ c.label }}
        </option>
      </select>
    </div>

    <!-- Address Line 1 -->
    <div class="form-group">
      <label :for="`${prefix}-address1`">Address Line 1</label>
      <input
        :id="`${prefix}-address1`"
        type="text"
        class="form-control"
        :value="modelValue.address_line1"
        placeholder="Street address"
        @input="emit('address_line1', $event.target.value)"
      />
    </div>

    <!-- Address Line 2 -->
    <div class="form-group">
      <label :for="`${prefix}-address2`">Address Line 2</label>
      <input
        :id="`${prefix}-address2`"
        type="text"
        class="form-control"
        :value="modelValue.address_line2"
        placeholder="Suite, unit, floor, etc."
        @input="emit('address_line2', $event.target.value)"
      />
    </div>

    <!-- City / State|Province / Postal Code row -->
    <div class="address-row">
      <div class="form-group city-field">
        <label :for="`${prefix}-city`">City</label>
        <input
          :id="`${prefix}-city`"
          type="text"
          class="form-control"
          :value="modelValue.city"
          @input="emit('city', $event.target.value)"
        />
      </div>

      <!-- US: State dropdown -->
      <div v-if="isUS" class="form-group region-field">
        <label :for="`${prefix}-state`">State</label>
        <select
          :id="`${prefix}-state`"
          class="form-control"
          :value="modelValue.state"
          @change="emit('state', $event.target.value)"
        >
          <option value="">-- State --</option>
          <option v-for="s in US_STATES" :key="s.value" :value="s.value">
            {{ s.value }} – {{ s.label }}
          </option>
        </select>
      </div>

      <!-- Canada: Province dropdown -->
      <div v-else-if="isCanada" class="form-group region-field">
        <label :for="`${prefix}-province`">Province</label>
        <select
          :id="`${prefix}-province`"
          class="form-control"
          :value="modelValue.province"
          @change="emit('province', $event.target.value)"
        >
          <option value="">-- Province --</option>
          <option v-for="p in CANADIAN_PROVINCES" :key="p.value" :value="p.value">
            {{ p.value }} – {{ p.label }}
          </option>
        </select>
      </div>

      <!-- Other country: free text region -->
      <div v-else class="form-group region-field">
        <label :for="`${prefix}-state-free`">State / Province / Region</label>
        <input
          :id="`${prefix}-state-free`"
          type="text"
          class="form-control"
          :value="modelValue.state || modelValue.province"
          placeholder="Region"
          @input="emit('state', $event.target.value)"
        />
      </div>

      <!-- Postal Code -->
      <div class="form-group postal-field">
        <label :for="`${prefix}-postal`">
          {{ isCanada ? 'Postal Code' : 'ZIP Code' }}
        </label>
        <input
          :id="`${prefix}-postal`"
          type="text"
          class="form-control"
          :value="modelValue.postal_code"
          :placeholder="isCanada ? 'A1A 1A1' : '12345'"
          :pattern="isUS ? '\\d{5}(-\\d{4})?' : undefined"
          :title="isUS ? '5-digit ZIP code (e.g. 90210)' : undefined"
          @input="emit('postal_code', $event.target.value)"
        />
        <small v-if="postalError" class="field-error">{{ postalError }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import { US_STATES, CANADIAN_PROVINCES, COUNTRIES } from '@/constants';

export default {
  name: 'AddressFields',

  props: {
    /**
     * The address object being edited. Expects:
     * { country, address_line1, address_line2, city, state, province, postal_code }
     */
    modelValue: {
      type: Object,
      required: true,
    },
    /** Unique prefix to avoid duplicate HTML id conflicts when multiple instances on page */
    prefix: {
      type: String,
      default: 'addr',
    },
    /** Whether the country field is required */
    required: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      US_STATES,
      CANADIAN_PROVINCES,
      COUNTRIES,
    };
  },

  computed: {
    isUS() {
      return this.modelValue.country === 'US';
    },
    isCanada() {
      return this.modelValue.country === 'CA';
    },
    postalError() {
      const code = this.modelValue.postal_code;
      if (!code) return null;
      if (this.isUS && !/^\d{5}(-\d{4})?$/.test(code)) {
        return 'Enter a valid ZIP code (e.g. 90210 or 90210-1234)';
      }
      if (this.isCanada && !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(code)) {
        return 'Enter a valid postal code (e.g. K1A 0A9)';
      }
      return null;
    },
  },

  methods: {
    emit(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
    onCountryChange(newCountry) {
      // When switching countries, clear region fields to avoid stale values
      this.$emit('update:modelValue', {
        ...this.modelValue,
        country: newCountry,
        state: '',
        province: '',
        postal_code: '',
      });
    },
  },
};
</script>

<style scoped>
.address-fields {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.address-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.city-field {
  flex: 2;
  min-width: 140px;
}

.region-field {
  flex: 1.2;
  min-width: 120px;
}

.postal-field {
  flex: 1;
  min-width: 100px;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.required-star {
  color: #dc3545;
  margin-left: 2px;
}

.form-control {
  display: block;
  width: 100%;
  padding: 9px 11px;
  font-size: 0.95rem;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #0d6efd;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.field-error {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 3px;
  display: block;
}

@media (max-width: 576px) {
  .address-row {
    flex-direction: column;
    gap: 0;
  }

  .city-field,
  .region-field,
  .postal-field {
    flex: unset;
    min-width: unset;
  }
}
</style>
